import React, { useCallback, useMemo, useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useGetHintCoverageMatrix } from '~/hooks/apis/connection-google/useHintCoverageMatrix';

import { HintCoverageMatrixCell, HintCoverageMatrixParams } from '~/services/ConnectionGoogleService';

const ABW_PAGE_SIZE = 30;
const LOS_PAGE_SIZE = 10;
const MAX_ABW = 300;
const MAX_LOS = 30;

type AccountType = HintCoverageMatrixParams['accountType'];

const ACCOUNT_TYPE_OPTIONS: Array<{ value: AccountType; label: string }> = [
  { value: 'lpc', label: 'LPC' },
  { value: 'lpp', label: 'LPP' },
  { value: 'scoopon', label: 'Scoopon' },
  { value: 'le_vacation_rental', label: 'LE Vacation Rental' },
];

function formatPct(pct: number): string {
  return `${pct.toFixed(1)}%`;
}

function formatNumber(num: number): string {
  return num.toLocaleString();
}

export default function HintCoverageMatrixContainer() {
  const theme = useTheme();
  const [accountType, setAccountType] = useState<AccountType>('lpc');
  const [windowHours, setWindowHours] = useState(5);
  const [abwPage, setAbwPage] = useState(0);
  const [losPage, setLosPage] = useState(0);

  const minAbw = abwPage * ABW_PAGE_SIZE + 1;
  const maxAbw = Math.min(minAbw + ABW_PAGE_SIZE - 1, MAX_ABW);
  const minLos = losPage * LOS_PAGE_SIZE + 1;
  const maxLos = Math.min(minLos + LOS_PAGE_SIZE - 1, MAX_LOS);

  const { data, isLoading, error, refetch } = useGetHintCoverageMatrix({
    accountType,
    windowHours,
    minAbw,
    maxAbw,
    minLos,
    maxLos,
  });

  const getCellColor = useCallback(
    (cell: HintCoverageMatrixCell): string => {
      const avgPct = (cell.high.pct + cell.medium.pct + cell.low.pct) / 3;

      if (avgPct >= 40) return alpha(theme.palette.success.main, 0.18);
      if (avgPct >= 25) return alpha(theme.palette.success.main, 0.12);
      if (avgPct >= 15) return alpha(theme.palette.warning.main, 0.18);
      if (avgPct >= 5) return alpha(theme.palette.warning.main, 0.12);
      return alpha(theme.palette.error.main, 0.16);
    },
    [theme],
  );

  const { abwValues, losValues, cellMap } = useMemo(() => {
    if (!data?.matrix) {
      return { abwValues: [], losValues: [], cellMap: new Map<string, HintCoverageMatrixCell>() };
    }

    const abwSet = new Set<number>();
    const losSet = new Set<number>();
    const map = new Map<string, HintCoverageMatrixCell>();

    for (const cell of data.matrix) {
      abwSet.add(cell.abw);
      losSet.add(cell.los);
      map.set(`${cell.abw}-${cell.los}`, cell);
    }

    return {
      abwValues: Array.from(abwSet).sort((a, b) => a - b),
      losValues: Array.from(losSet).sort((a, b) => a - b),
      cellMap: map,
    };
  }, [data]);

  const triangleIconSx = {
    width: 0,
    height: 0,
    borderLeft: `${theme.spacing(0.75)} solid transparent`,
    borderRight: `${theme.spacing(0.75)} solid transparent`,
  };

  return (
    <Container maxWidth="xl">
      <PageSubheader title="Pull With Hints Coverage">
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: theme.spacing(22.5) }}>
            <InputLabel>Account Type</InputLabel>
            <Select value={accountType} label="Account Type" onChange={(e) => setAccountType(e.target.value as AccountType)}>
              {ACCOUNT_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Window (hrs)"
            type="number"
            value={windowHours}
            onChange={(e) => setWindowHours(Math.max(1, Math.min(24, Number(e.target.value) || 1)))}
            slotProps={{ htmlInput: { min: 1, max: 24 } }}
            sx={{ width: theme.spacing(15) }}
          />

          <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Stack>
      </PageSubheader>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error.message || 'Failed to load hint coverage data'}
        </Alert>
      )}

      {data && !isLoading && (
        <Box mt={2}>
          <Stack direction="row" spacing={2} mb={2}>
            <Chip
              icon={
                <Box
                  sx={{
                    width: theme.spacing(1.5),
                    height: theme.spacing(1.5),
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                  }}
                />
              }
              label={`HIGH: ${formatNumber(data.tierTotals.HIGH)} properties`}
              variant="outlined"
              sx={{ borderColor: 'success.main', bgcolor: alpha(theme.palette.success.main, 0.12) }}
            />
            <Chip
              icon={
                <Box
                  sx={{
                    ...triangleIconSx,
                    borderBottom: `${theme.spacing(1.5)} solid ${theme.palette.warning.main}`,
                  }}
                />
              }
              label={`MEDIUM: ${formatNumber(data.tierTotals.MEDIUM)} properties`}
              variant="outlined"
              sx={{ borderColor: 'warning.main', bgcolor: alpha(theme.palette.warning.main, 0.12) }}
            />
            <Chip
              icon={
                <Box
                  sx={{
                    ...triangleIconSx,
                    borderBottom: `${theme.spacing(1.5)} solid ${theme.palette.error.main}`,
                  }}
                />
              }
              label={`LOW: ${formatNumber(data.tierTotals.LOW)} properties`}
              variant="outlined"
              sx={{ borderColor: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.12) }}
            />
          </Stack>

          <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Box
              component="table"
              sx={{
                borderCollapse: 'collapse',
                width: '100%',
                minWidth: abwValues.length * 90,
                '& th, & td': {
                  border: 1,
                  borderColor: 'divider',
                  px: 0.75,
                  py: 0.5,
                  textAlign: 'center',
                  typography: 'caption',
                  whiteSpace: 'nowrap',
                },
                '& th': {
                  bgcolor: 'grey.100',
                  fontWeight: theme.typography.fontWeightBold,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                },
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  <Box
                    component="th"
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: 'grey.200',
                      minWidth: theme.spacing(7.5),
                    }}
                  >
                    <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                      ABW →
                    </Typography>
                    <br />
                    <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                      LOS ↓
                    </Typography>
                  </Box>
                  {abwValues.map((abw) => (
                    <Box component="th" key={abw} sx={{ minWidth: theme.spacing(10) }}>
                      {abw}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {losValues.map((los) => (
                  <Box component="tr" key={los}>
                    <Box
                      component="td"
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        bgcolor: 'grey.100',
                        fontWeight: theme.typography.fontWeightBold,
                      }}
                    >
                      {los}
                    </Box>
                    {abwValues.map((abw) => {
                      const cell = cellMap.get(`${abw}-${los}`);
                      if (!cell) {
                        return (
                          <Box component="td" key={abw} sx={{ bgcolor: 'grey.50' }}>
                            —
                          </Box>
                        );
                      }

                      const bgColor = getCellColor(cell);

                      return (
                        <Tooltip
                          key={abw}
                          title={
                            <Box>
                              <Typography variant="caption" display="block">
                                ABW: {abw}, LOS: {los}
                              </Typography>
                              <Typography variant="caption" display="block">
                                HIGH: {cell.high.served} served ({formatPct(cell.high.pct)})
                              </Typography>
                              <Typography variant="caption" display="block">
                                MEDIUM: {cell.medium.served} served ({formatPct(cell.medium.pct)})
                              </Typography>
                              <Typography variant="caption" display="block">
                                LOW: {cell.low.served} served ({formatPct(cell.low.pct)})
                              </Typography>
                              {cell.lastServedAt && (
                                <Typography variant="caption" display="block">
                                  Last served: {new Date(cell.lastServedAt).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                          }
                          arrow
                        >
                          <Box component="td" sx={{ bgcolor: bgColor, cursor: 'default' }}>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ color: 'success.main', fontWeight: theme.typography.fontWeightMedium }}
                            >
                              H: {formatPct(cell.high.pct)}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ color: 'warning.main' }}>
                              M: {formatPct(cell.medium.pct)}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ color: 'error.main' }}>
                              L: {formatPct(cell.low.pct)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              Showing ABW {minAbw}-{maxAbw}, LOS {minLos}-{maxLos}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" disabled={abwPage === 0} onClick={() => setAbwPage((p) => p - 1)}>
                ← Prev ABW
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={maxAbw >= MAX_ABW}
                onClick={() => setAbwPage((p) => p + 1)}
              >
                Next ABW →
              </Button>
              <Button size="small" variant="outlined" disabled={losPage === 0} onClick={() => setLosPage((p) => p - 1)}>
                ↑ Prev LOS
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={maxLos >= MAX_LOS}
                onClick={() => setLosPage((p) => p + 1)}
              >
                Next LOS ↓
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Container>
  );
}
